import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ProduccionB2bShell } from "@/components/b2b/ProduccionB2bShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable, CrudEmptyState, Panel } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { apiGetJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import { useTenantContext } from "@/lib/tenant-context";
import { b2bCopy } from "@/lib/merchant-feature-copy";

export const Route = createFileRoute("/app/produccion/tablero")({
  component: ProduccionTableroPage,
  head: () => ({ meta: [{ title: "Tablero — B2B / Mayorista" }] }),
});

interface ProductionData {
  date: string;
  ordersCount: number;
  plates: { name: string; totalQty: number; institutions: string[] }[];
}

function ProduccionTableroPage() {
  const tid = useWorkspaceTenantId();
  const { businessProfile } = useTenantContext();
  const copy = b2bCopy(businessProfile);
  const [production, setProduction] = useState<ProductionData | null>(null);
  const [prodDate, setProdDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    if (!tid) return;
    try {
      const p = await apiGetJson<ProductionData>(`/api/institutional/production?date=${prodDate}`, tid);
      setProduction(p);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar tablero");
    }
  }, [tid, prodDate]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: DataTableColumn<ProductionData["plates"][number]>[] = [
    { key: "plato", header: copy.itemColumn, render: (p) => <span className="font-medium">{p.name}</span> },
    { key: "cantidad", header: "Cantidad total", className: "text-right", render: (p) => p.totalQty },
    {
      key: "instituciones",
      header: "Instituciones",
      render: (p) => <span className="text-xs text-muted-foreground">{p.institutions.join(", ") || "—"}</span>,
    },
  ];

  return (
    <ProduccionB2bShell title={copy.boardTitle}>
      <Panel title="Consolidado por fecha">
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>{copy.boardDateLabel}</Label>
              <Input
                type="date"
                value={prodDate}
                onChange={(e) => setProdDate(e.target.value)}
                className="mt-1 w-40"
              />
            </div>
            <Button variant="secondary" onClick={() => void load()}>
              Actualizar
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {production?.ordersCount ?? 0} pedidos institucionales ese día
          </p>
          <DataTable
            columns={columns}
            rows={production?.plates ?? []}
            getRowKey={(p) => p.name}
            emptyState={<CrudEmptyState title="Sin pedidos consolidados para esta fecha" />}
          />
        </div>
      </Panel>
    </ProduccionB2bShell>
  );
}
